import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Select, Spin, Empty } from 'antd';
import { USER_ROLES } from 'constants/users';
import { _debounce } from '@medicusunion/front-utils';
import { fetchUsers } from 'services/api/users';
import { UserListRow } from 'types/api/users';

const PAGE_LIMIT = 20;

const debounce = _debounce();

const PatientSelect: FC = () => {
  const { t } = useTranslation();
  const [patientName, setPatientName] = useState('');
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [results, setResults] = useState<UserListRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        // Patient option list deliberately spans all organizations.
        const res = await fetchUsers(
          {
            page: 1,
            limit: PAGE_LIMIT,
            role: USER_ROLES.patient,
            name: patientName
          },
          { headers: { organization: null as unknown as string } }
        );

        if (!cancelled) setResults(res.results);
      } catch {
        // swallow — empty list on failure
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [patientName]);

  const patients = results.map(item => ({
    label: `${item.first_name} ${item.last_name} (${item.email})`,
    value: item.id
  }));

  return (
    <Form.Item
      name="patient_id"
      label={t('appointments.patient')}
      rules={[{ required: true }]}
    >
      <Select
        size="large"
        filterOption={false}
        options={patients}
        loading={loading}
        notFoundContent={
          patientSearchLoading || loading ? (
            <Spin
              size="small"
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 134
              }}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )
        }
        onSearch={input => {
          if (!patientSearchLoading) {
            setPatientSearchLoading(true);
          }

          debounce(() => {
            setPatientName(input);
            setPatientSearchLoading(false);
          });
        }}
        showSearch
        placeholder={t('appointments.patient_placeholder')}
        style={{ width: '100%' }}
      />
    </Form.Item>
  );
};

export default PatientSelect;
